import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../UI/Logo";
import NavItem from "../UI/NavItem";
import { NavSection } from "../../types";

import LobbyIcon from "../../assets/icons/home.svg";
import RecentStakesIcon from "../../assets/icons/recent.svg";
import LeaderboardsIcon from "../../assets/icons/leader.svg";
import CoinflipIcon from "../../assets/icons/flip.svg";
import PantherWheelIcon from "../../assets/icons/wheel.svg";
import FourheadOrderIcon from "../../assets/icons/banner.svg";
import RafflesIcon from "../../assets/icons/gift.svg";
import ReferralsIcon from "../../assets/icons/people.svg";
import BlogIcon from "../../assets/icons/books.svg";
import BecomeCreatorIcon from "../../assets/icons/sparkle.svg";
import HelpSupportIcon from "../../assets/icons/headset.svg";
import HamburgerIcon from "../../assets/icons/hamburger.svg";
// import DarkLineIcon from "../../assets/icons/dark-line.svg";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const iconMap = {
    lobby: LobbyIcon,
    "recent-stakes": RecentStakesIcon,
    leaderboards: LeaderboardsIcon,
    coinflip: CoinflipIcon,
    "panther-wheel": PantherWheelIcon,
    "fourhead-order": FourheadOrderIcon,
    raffles: RafflesIcon,
    referrals: ReferralsIcon,
    blog: BlogIcon,
    "become-creator": BecomeCreatorIcon,
    "help-support": HelpSupportIcon,
  };

  const navSections: NavSection[] = [
    {
      title: "",
      items: [
        { id: "lobby", label: "Lobby", path: "/lobby" },
        { id: "recent-stakes", label: "Recent Stakes", path: "/recent-stakes" },
        { id: "leaderboards", label: "Leaderboards", path: "/leaderboards" },
      ],
    },
    {
      title: "Games",
      items: [
        { id: "coinflip", label: "Coinflip", path: "/games/coinflip" },
        {
          id: "panther-wheel",
          label: "Panther Wheel",
          path: "/games/panther-wheel",
        },
        {
          id: "fourhead-order",
          label: "Fourhead Order",
          path: "/games/fourhead-order",
        },
      ],
    },
    {
      title: "Explore",
      items: [
        { id: "raffles", label: "Raffles", path: "/explore/raffles" },
        { id: "referrals", label: "Referrals", path: "/explore/referrals" },
        { id: "blog", label: "Blog", path: "/explore/blog" },
      ],
    },
  ];

  const bottomItems = [
    {
      id: "become-creator",
      label: "Become a Creator",
      path: "/become-creator",
    },
    { id: "help-support", label: "Help & Support", path: "/help-support" },
  ];

  // Mobile bottom nav items (only 3 items)
  const mobileBottomNavItems = [
    { id: "lobby", label: "Lobby", path: "/lobby" },
    { id: "coinflip", label: "Coinflip", path: "/games/coinflip" },
    { id: "raffles", label: "Raffles", path: "/explore/raffles" },
  ];

  // Menu items for hamburger dropdown
  const menuItems = [
    { id: "recent-stakes", label: "Recent Stakes", path: "/recent-stakes" },
    { id: "leaderboards", label: "Leaderboards", path: "/leaderboards" },
    { id: "referrals", label: "Referrals", path: "/explore/referrals" },
    {
      id: "become-creator",
      label: "Become a Creator",
      path: "/become-creator",
    },
    { id: "blog", label: "Blog", path: "/explore/blog" },
    { id: "help-support", label: "Help & Support", path: "/help-support" },
  ];

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="sidebar">
        <Logo />

        <nav className="sidebar__nav">
          {navSections.map((section) => (
            <div key={section.title} className="nav-section">
              <h3 className="nav-section__title">{section.title}</h3>
              {section.items.map((item) => {
                const iconSrc = iconMap[item.id as keyof typeof iconMap];
                return (
                  <NavItem
                    key={item.id}
                    label={item.label}
                    path={item.path}
                    isChild={true}
                    icon={iconSrc}
                  />
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar__bottom">
          {bottomItems.map((item) => {
            const iconSrc = iconMap[item.id as keyof typeof iconMap];
            return (
              <NavItem
                key={item.id}
                label={item.label}
                path={item.path}
                icon={iconSrc}
              />
            );
          })}
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        {/* Main navigation container (rounded pill) */}
        <div className="mobile-bottom-nav__container">
          {mobileBottomNavItems.map((item) => {
            const iconSrc = iconMap[item.id as keyof typeof iconMap];
            const isActive = location.pathname === item.path;

            return (
              <div
                key={item.id}
                className={`mobile-bottom-nav__item ${
                  isActive ? "active" : ""
                }`}
                onClick={() => navigate(item.path)}
              >
                <img
                  src={iconSrc}
                  alt={`${item.label} icon`}
                  className="mobile-bottom-nav__icon"
                />
                <span className="mobile-bottom-nav__label">{item.label}</span>
              </div>
            );
          })}
        </div>

        {/* Standalone Hamburger Menu Button */}
        <div className="mobile-bottom-nav__hamburger-wrapper">
          <div
            className={`mobile-bottom-nav__hamburger ${
              isMenuOpen ? "active" : ""
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <img
              src={HamburgerIcon}
              alt="Menu"
              className="mobile-bottom-nav__hamburger-icon"
            />
          </div>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="mobile-menu-dropdown">
              {menuItems.map((item, index) => {
                const iconSrc = iconMap[item.id as keyof typeof iconMap];
                const isActive = location.pathname === item.path;
                // const needsSeparator =
                //   index === 1 || index === 2 || index === 3; // After leaderboards, referrals, become-creator

                return (
                  <React.Fragment key={item.id}>
                    <div
                      className={`mobile-menu-dropdown__item ${
                        isActive ? "active" : ""
                      }`}
                      onClick={() => handleMenuItemClick(item.path)}
                    >
                      <img
                        src={iconSrc}
                        alt={`${item.label} icon`}
                        className="mobile-menu-dropdown__icon"
                      />
                      <span className="mobile-menu-dropdown__label">
                        {item.label}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Backdrop for closing menu */}
      {isMenuOpen && (
        <div
          className="mobile-menu-backdrop"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
